import { formatCents, parseMoneyToCents, tt } from '@agos/shared'

/**
 * `/settings-franchise` — nineteen fields, one stored key, and nothing reads any of them.
 *
 * **FB-137.** `GET /settings/franchise` on the restored data answers exactly this:
 *
 * ```json
 * {"franchiseName": ""}
 * ```
 *
 * One key, empty. The other eighteen fields on the legacy page are literals in the React file, and
 * `setS({ ...DEFAULTS, ...data })` merges them under whatever the server sent — so a field that has never
 * been stored is drawn identically to one that has. Six of those literals read as commercial terms:
 *
 * | Field | Legacy default | What the platform actually bills |
 * |---|---|---|
 * | `licenceFeePercent` *(percento z tržieb odvádzané centrále)* | **5** | `platform_billing_settings.revenue_share_pct` = **1,00**, billed by `BillingRunner` as `1% z tržieb` |
 * | `smsPrice` | 0,035 € | no SMS row in `api_service_pricing` at all |
 * | `firstDeviceFee` / `additionalDeviceFee` | 40 € / 10 € | no device row either; hardware is invoiced per line (*Hardvér: Fiskálna tlačiareň N5*) |
 * | `billingCoefficient` | 1 | nothing reads it |
 * | `fixedBillingPrice` | 0 € | module and service pricing live in `franchise_module_billing` and `api_service_pricing` |
 *
 * So the page shows this franchise paying five times the rate the platform bills, and typing a different
 * number changes neither. **Two files in the whole legacy codebase mention these keys** — the page itself
 * and the zod schema on its own write path. Not one report, invoice or job reads them.
 *
 * `appName` is the sharpest case: the page's *Názov aplikácie* writes a key nobody reads, while the name
 * actually used on outgoing mail is the Spring property `agos.app-name` (default `PizzaMajstro`), which
 * this screen cannot reach. And the one key that *is* stored, `franchiseName`, duplicates
 * `central_hub.franchises.name` — authoritative, and holding *Pizza Majstro Šurany* while the copy here is
 * an empty string.
 *
 * **The selects are worse than decorative.** `DEFAULTS.franchise = tt("Žiadny")` is translated at module
 * load, while the option is the literal `<option value="Žiadny">`. Under any non-Slovak language the state
 * holds a translated string that matches no option — the select renders as though the first were chosen,
 * and the save stores the translated string. What ends up in the database depends on the language of the
 * browser that last pressed save. Three of the four selects also offer **one option each**, which is a
 * statement drawn as a choice.
 *
 * The port keeps every field, because deleting a franchise's record of its own terms is the owner's call
 * and not a porting decision. What it does not do is let a literal pass for an agreement.
 */

export type BillingMethod = 'none' | 'percent_of_orders' | 'fixed_amount'

/** Fields the system reads somewhere. On this section: none. */
export const READ_BY_NOTHING: readonly string[] = [
  'appName',
  'franchiseId',
  'billingEnabled',
  'billingCoefficient',
  'smsPrice',
  'fixedBillingPrice',
  'franchise',
  'commissionPartner',
  'franchiseBillingMethod',
  'fastkitchenBillingMethod',
  'firstDeviceFee',
  'additionalDeviceFee',
  'cardpayCommission',
  'licenceFeePercent',
  'centralMenuControl',
  'sharedReports',
  'centralDashboardUrl',
  'franchiserContact',
  'autoSync',
]

/**
 * The legacy defaults, kept as data rather than as initial state.
 *
 * They are shown as *what legacy would have put here*, offered on a button, and never pre-filled — a
 * number nobody stored must not be indistinguishable from one somebody agreed to.
 */
export const LEGACY_DEFAULTS: Readonly<Record<string, string>> = {
  billingCoefficient: '1',
  smsPrice: '0,035',
  fixedBillingPrice: '0',
  firstDeviceFee: '40',
  additionalDeviceFee: '10',
  licenceFeePercent: '5',
}

/** The rate the platform actually bills, for the one figure where the contradiction is checkable. */
export const PLATFORM_REVENUE_SHARE_PCT = 1

export interface FranchiseSettings {
  appName?: string
  franchiseId?: string
  franchiseName?: string
  billingEnabled?: boolean
  billingCoefficient?: number | string
  smsPrice?: number | string
  fixedBillingPrice?: number | string
  franchise?: string
  commissionPartner?: string
  franchiseBillingMethod?: string
  fastkitchenBillingMethod?: string
  firstDeviceFee?: number | string
  additionalDeviceFee?: number | string
  cardpayCommission?: number | string
  licenceFeePercent?: number | string
  centralMenuControl?: boolean
  sharedReports?: boolean
  centralDashboardUrl?: string
  franchiserContact?: string
  autoSync?: boolean
}

export interface FranchiseForm {
  appName: string
  franchiseId: string
  franchiseName: string
  billingEnabled: boolean
  billingCoefficient: string
  smsPrice: string
  fixedBillingPrice: string
  franchise: string
  commissionPartner: string
  franchiseBillingMethod: BillingMethod
  fastkitchenBillingMethod: BillingMethod
  firstDeviceFee: string
  additionalDeviceFee: string
  cardpayCommission: string
  licenceFeePercent: string
  centralMenuControl: boolean
  sharedReports: boolean
  centralDashboardUrl: string
  franchiserContact: string
  autoSync: boolean
}

/** The numeric fields, all of which are text in the form so *unset* survives a round trip. */
export const NUMERIC_FIELDS = [
  'billingCoefficient',
  'smsPrice',
  'fixedBillingPrice',
  'firstDeviceFee',
  'additionalDeviceFee',
  'cardpayCommission',
  'licenceFeePercent',
] as const

export type NumericField = (typeof NUMERIC_FIELDS)[number]

/** The four money fields among them — cents, per §14.7. */
export const MONEY_FIELDS: readonly NumericField[] = [
  'smsPrice',
  'fixedBillingPrice',
  'firstDeviceFee',
  'additionalDeviceFee',
  'cardpayCommission',
]

/**
 * A stored value as text.
 *
 * The section is a free-form blob, so a key can hold anything — including an object, if some other
 * writer put one there. Anything that is not a string, number or boolean is treated as absent rather
 * than rendered as `[object Object]` into a field somebody might then save back.
 */
function text(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return String(value)
  return ''
}

/**
 * Read the section, keeping *absent* absent.
 *
 * Legacy spreads its defaults underneath, so an unstored `licenceFeePercent` arrives as `5` and reads as a
 * rate somebody agreed. Here an absent key is an empty string, and the page says so.
 */
export function formOf(settings: FranchiseSettings | undefined): FranchiseForm {
  return {
    appName: text(settings?.appName),
    franchiseId: text(settings?.franchiseId),
    franchiseName: text(settings?.franchiseName),
    billingEnabled: settings?.billingEnabled === true,
    billingCoefficient: text(settings?.billingCoefficient),
    smsPrice: text(settings?.smsPrice),
    fixedBillingPrice: text(settings?.fixedBillingPrice),
    franchise: text(settings?.franchise),
    commissionPartner: text(settings?.commissionPartner),
    franchiseBillingMethod: methodOf(settings?.franchiseBillingMethod),
    fastkitchenBillingMethod: methodOf(settings?.fastkitchenBillingMethod),
    firstDeviceFee: text(settings?.firstDeviceFee),
    additionalDeviceFee: text(settings?.additionalDeviceFee),
    cardpayCommission: text(settings?.cardpayCommission),
    licenceFeePercent: text(settings?.licenceFeePercent),
    centralMenuControl: settings?.centralMenuControl === true,
    sharedReports: settings?.sharedReports === true,
    centralDashboardUrl: text(settings?.centralDashboardUrl),
    franchiserContact: text(settings?.franchiserContact),
    autoSync: settings?.autoSync === true,
  }
}

/**
 * Which keys the section actually holds.
 *
 * The distinction legacy erases. Everything else on screen is a default nobody stored, and each of those
 * fields says which of the two it is.
 */
export function storedKeys(settings: FranchiseSettings | undefined): string[] {
  if (settings === undefined || settings === null) return []
  return Object.entries(settings)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key]) => key)
    .sort()
}

// ─── The selects ──────────────────────────────────────────────────────────────

/**
 * Read a stored billing method, whatever language it was saved in.
 *
 * The legacy values are Slovak display strings produced by `tt()` at module load, so a section written
 * from an English browser holds `"None"` and one written from a Slovak browser holds `"Žiadny"` — the same
 * choice under two spellings. Both map to the same key here, and the key is what gets stored from now on.
 */
/**
 * The spellings found in the wild, per method.
 *
 * These are **stored values, not labels** — they are what legacy wrote into the database in whichever
 * language the browser had loaded, so they have to be matched literally and must not go through `tt()`.
 * The label a reader sees comes from `methodLabel`.
 */
const STORED_SPELLINGS: Readonly<Record<Exclude<BillingMethod, 'none'>, readonly string[]>> = {
  // eslint-disable-next-line no-restricted-syntax -- stored database values, not user-visible text
  percent_of_orders: ['percent_of_orders', 'percento zo všetkých objednávok', 'percentage of all orders'],
  // eslint-disable-next-line no-restricted-syntax -- stored database values, not user-visible text
  fixed_amount: ['fixed_amount', 'fixná suma', 'fixed amount'],
}

export function methodOf(value: unknown): BillingMethod {
  const raw = text(value).trim().toLowerCase()
  if (raw === '') return 'none'
  for (const [method, spellings] of Object.entries(STORED_SPELLINGS)) {
    if (spellings.includes(raw)) return method as BillingMethod
  }
  return 'none'
}

export function methodLabel(method: BillingMethod): string {
  if (method === 'percent_of_orders') return tt('Percento zo všetkých objednávok')
  if (method === 'fixed_amount') return tt('Fixná suma')
  return tt('Žiadny')
}

/**
 * The options a select genuinely offers.
 *
 * `franchise`, `commissionPartner` and `franchiseBillingMethod` have exactly one option each in legacy, so
 * they are not choices. They are rendered as the statement they are rather than as a dropdown that cannot
 * drop.
 */
export const SINGLE_OPTION_FIELDS: readonly string[] = [
  'franchise',
  'commissionPartner',
  'franchiseBillingMethod',
]

// ─── The numbers ──────────────────────────────────────────────────────────────

/** A percentage is a multiplicand (FB-056), so it is never rounded before use — only parsed. */
export function percentOf(value: string): number | null {
  const raw = value.trim().replace(',', '.')
  if (raw === '') return null
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return null
  return parsed
}

export function centsOf(value: string): number | null {
  const raw = value.trim()
  if (raw === '') return null
  const parsed = parseMoneyToCents(raw)
  return parsed < 0 ? null : parsed
}

export type NumberProblem = 'not-a-number' | 'negative' | 'over-100' | null

export function numberProblem(field: NumericField, value: string): NumberProblem {
  const raw = value.trim()
  if (raw === '') return null
  const parsed = Number(raw.replace(',', '.'))
  if (!Number.isFinite(parsed)) return 'not-a-number'
  if (parsed < 0) return 'negative'
  if (field === 'licenceFeePercent' && parsed > 100) return 'over-100'
  return null
}

export function numberProblemMessage(problem: NumberProblem): string {
  if (problem === 'not-a-number') return tt('Toto nie je číslo.')
  if (problem === 'negative') return tt('Záporná suma tu nemá zmysel.')
  if (problem === 'over-100') return tt('Percento z tržieb nemôže byť viac ako 100.')
  return ''
}

export function problemFields(form: FranchiseForm): NumericField[] {
  return NUMERIC_FIELDS.filter((field) => numberProblem(field, form[field]) !== null)
}

/**
 * Whether the licence fee on screen contradicts the rate the platform bills.
 *
 * `null` when the field is empty — an unstored figure is not a disagreement, it is an absence, and the
 * page says that separately. The two only contradict each other once somebody has typed one.
 */
export function feeDisagreesWithPlatform(form: FranchiseForm): boolean {
  const percent = percentOf(form.licenceFeePercent)
  return percent !== null && percent !== PLATFORM_REVENUE_SHARE_PCT
}

// ─── The save ─────────────────────────────────────────────────────────────────

/**
 * What to send.
 *
 * The section **merges**, so only the keys the form actually holds are sent — an empty field is omitted
 * rather than written as `""`, because writing an empty string into a merging section makes *never set*
 * unreachable for every future reader. A boolean is always sent: `false` is a value there, not an absence.
 */
export function payloadOf(form: FranchiseForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    billingEnabled: form.billingEnabled,
    centralMenuControl: form.centralMenuControl,
    sharedReports: form.sharedReports,
    autoSync: form.autoSync,
    // Stored as stable keys, not as translated display strings.
    franchiseBillingMethod: form.franchiseBillingMethod,
    fastkitchenBillingMethod: form.fastkitchenBillingMethod,
  }

  for (const field of ['appName', 'franchiseId', 'franchiseName', 'centralDashboardUrl', 'franchiserContact', 'franchise', 'commissionPartner'] as const) {
    const value = form[field].trim()
    if (value !== '') payload[field] = value
  }

  for (const field of NUMERIC_FIELDS) {
    const raw = form[field].trim()
    if (raw === '') continue
    if (MONEY_FIELDS.includes(field)) {
      const cents = centsOf(raw)
      if (cents !== null) payload[field] = cents / 100
      continue
    }
    const parsed = Number(raw.replace(',', '.'))
    if (Number.isFinite(parsed)) payload[field] = parsed
  }

  return payload
}

/** What a field is: stored, or a default the page is only suggesting. */
export function fieldState(field: string, form: FranchiseForm, settings: FranchiseSettings | undefined): 'stored' | 'unset' {
  return storedKeys(settings).includes(field) ||
    (field in form && String(form[field as keyof FranchiseForm]).trim() !== '')
    ? 'stored'
    : 'unset'
}

export function suggestionFor(field: string): string | null {
  return LEGACY_DEFAULTS[field] ?? null
}

/** The money suggestion, formatted the way the field will read it back. */
export function suggestionLabel(field: string): string {
  const suggestion = suggestionFor(field)
  if (suggestion === null) return ''
  if (MONEY_FIELDS.includes(field as NumericField)) {
    const cents = centsOf(suggestion)
    return cents === null ? suggestion : formatCents(cents)
  }
  return suggestion
}
